import { api } from "@/redux/api";

import {
	McpConnection,
	McpConnectionListSchema,
	McpConsentInfo,
	McpConsentInfoSchema,
	McpConsentResult,
	McpConsentResultSchema,
} from "./mcpOauth.schema";

/**
 * The browser-facing half of the MCP OAuth flow (docs/brief-mcp-server.md §"Tier 4"): reading a
 * pending authorization request, approving or denying it, and managing the applications the user
 * has already connected. Everything else — registration, token exchange, refresh — happens
 * directly between the MCP client and the API.
 */
export const mcpOauthApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMcpConsentInfo: builder.query<McpConsentInfo, string>({
			query: (requestId) => ({
				url: `/oauth/consent/${requestId}`,
				method: "GET",
				responseSchema: McpConsentInfoSchema,
			}),
		}),
		decideMcpConsent: builder.mutation<McpConsentResult, { requestId: string; approve: boolean }>({
			query: ({ requestId, approve }) => ({
				url: `/oauth/consent/${requestId}`,
				method: "POST",
				body: { approve },
				responseSchema: McpConsentResultSchema,
			}),
			invalidatesTags: ["McpConnections"],
		}),
		getMcpConnections: builder.query<McpConnection[], void>({
			query: () => ({
				url: "/oauth/connections",
				method: "GET",
				responseSchema: McpConnectionListSchema,
			}),
			providesTags: ["McpConnections"],
		}),
		revokeMcpConnection: builder.mutation<{ success: boolean }, string>({
			query: (clientId) => ({
				url: `/oauth/connections/${clientId}/revoke`,
				method: "POST",
			}),
			invalidatesTags: ["McpConnections"],
		}),
	}),
});

export const {
	useGetMcpConsentInfoQuery,
	useDecideMcpConsentMutation,
	useGetMcpConnectionsQuery,
	useRevokeMcpConnectionMutation,
} = mcpOauthApi;
export const mcpOauthApiReducer = mcpOauthApi.reducer;
export const mcpOauthApiMiddleware = mcpOauthApi.middleware;
